const AUTH_URL = import.meta.env.VITE_AUTH_SERVER || 'http://localhost:8787';
const TOKEN_KEY = 'draftresume_auth_token';
const USER_KEY = 'draftresume_auth_user';

export interface AuthUser {
  id: string;
  email: string;
  token_balance: number;
  promo_code: string;
  stripe_customer_id?: string | null;
  // 1 if the user has a Stripe customer record or any purchase history;
  // controls whether the "Manage billing" button is shown on AccountPage.
  has_billing?: 0 | 1;
  // 1 when the user has an active paid subscription (derived server-side from
  // subscription_active AND subscription_ends_at > now). Gates subscriber-only
  // features like AI bullet suggestions.
  is_subscriber?: 0 | 1;
  subscription_ends_at?: number | null;
}

export interface BulletSuggestion {
  summary: string;
  tips: string[];
  rewriteSuggestion: string;
  keywords: string[];
  cached: boolean;
}

export interface InterviewPrepBullet {
  experience_index: number;
  bullet_index: number;
  talking_point: string;
  if_pressed?: string;
}

export interface InterviewPrep {
  bullets: InterviewPrepBullet[];
  cached: boolean;
  cost_units: number;
  balance_after: number;
  generated_at: number;
}

export type InterviewPrepResult =
  | { ok: true; data: InterviewPrep }
  | { ok: false; reason: 'unauthenticated' }
  | { ok: false; reason: 'insufficient'; cost?: number; balance?: number }
  | { ok: false; reason: 'error'; message: string };

/**
 * Generate per-bullet interview talking points for a tailored resume.
 * Server-side: cache-first (free serve), atomic deduct, refund-on-LLM-fail.
 * Cost is 10 units (1 token) for non-subscribers, 3 units (~0.3) for active
 * subs. Anon callers are rejected with `unauthenticated`.
 */
export async function getInterviewPrep(
  resumeJson: object,
  jobDescription: string,
  jobTitle?: string,
): Promise<InterviewPrepResult> {
  const token = getToken();
  if (!token) return { ok: false, reason: 'unauthenticated' };
  try {
    const res = await fetch(`${AUTH_URL}/api/interview-prep`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ resume_json: resumeJson, job_description: jobDescription, ...(jobTitle ? { job_title: jobTitle } : {}) }),
    });
    const data = await res.json().catch(() => ({})) as Record<string, unknown>;
    if (res.ok) return { ok: true, data: data as unknown as InterviewPrep };
    if (res.status === 402) {
      return {
        ok: false,
        reason: 'insufficient',
        cost: typeof data.cost === 'number' ? data.cost : undefined,
        balance: typeof data.balance === 'number' ? data.balance : undefined,
      };
    }
    if (res.status === 401) return { ok: false, reason: 'unauthenticated' };
    return { ok: false, reason: 'error', message: typeof data.error === 'string' ? data.error : 'Could not generate interview prep' };
  } catch {
    return { ok: false, reason: 'error', message: 'Network error generating interview prep' };
  }
}

/**
 * Get AI-generated improvement advice for a single resume bullet. Subscriber
 * gate + rate limit + caching are enforced server-side.
 */
export async function getBulletSuggestion(
  bulletText: string,
  context: { sectionTitle?: string; jobDescription?: string; jobTitle?: string }
): Promise<BulletSuggestion> {
  const token = getToken();
  if (!token) throw new Error('Sign in to get AI suggestions');
  const res = await fetch(`${AUTH_URL}/api/resume-suggestion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ bulletText, ...context }),
  });
  const data = await res.json();
  if (!res.ok) {
    if (res.status === 402) throw new Error('Subscriber feature - upgrade to get AI suggestions.');
    if (res.status === 429) throw new Error(data.error || 'Daily limit reached.');
    throw new Error(data.error || 'Could not get suggestion');
  }
  return data as BulletSuggestion;
}

/**
 * Open the Stripe Customer Portal in the current tab. Users can cancel their
 * subscription, update payment methods, and view past invoices. Returns them
 * to /account when they're done.
 */
export async function openBillingPortal(): Promise<void> {
  const token = getToken();
  if (!token) throw new Error('Not signed in');
  const res = await fetch(`${AUTH_URL}/auth/billing-portal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
  });
  const data = await res.json();
  if (!res.ok || !data.url) throw new Error(data.error || 'Could not open billing portal');
  window.location.href = data.url;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export type DeductType = 'full' | 'section' | 'inline';
export type DeductInput = { type: DeductType } | { amount: number };
export type DeductOutcome =
  | { ok: true; deductId: string | null }
  | { ok: false; reason: 'unauthenticated' | 'insufficient' | 'error' };

/**
 * Charge the signed-in user for an AI action via /auth/deduct (atomic
 * conditional UPDATE on user.token_balance). Returns a typed outcome the
 * caller can branch on:
 *
 *   - 'unauthenticated' -> caller should send the user to /login
 *   - 'insufficient'    -> caller should open the purchase modal
 *   - 'error'           -> caller should surface a generic failure
 *
 * Two input shapes are supported, matching the server contract:
 *   - { type: 'full' | 'section' | 'inline' } for fixed-cost actions
 *     (server looks up 10 / 10 / 1 units respectively)
 *   - { amount: N } for custom-cost actions like cover letter and Vibe
 *
 * MUST be called BEFORE the AI call so we don't burn gateway dollars on
 * users who can't pay; if the AI call subsequently fails the user is
 * still charged (matching Step4Review's pre-existing pattern).
 */
export async function deductTokens(input: DeductInput): Promise<DeductOutcome> {
  const token = getToken();
  if (!token) return { ok: false, reason: 'unauthenticated' };
  try {
    const res = await fetch(`${AUTH_URL}/auth/deduct`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(input),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({})) as { deduct_id?: string };
      return { ok: true, deductId: data.deduct_id ?? null };
    }
    if (res.status === 402) return { ok: false, reason: 'insufficient' };
    return { ok: false, reason: 'error' };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/**
 * Reverse a previously-charged deduct. Use when the AI call AFTER deduct
 * fails (empty 200, gateway timeout, etc.) so the user doesn't lose tokens
 * on a server-side failure they can't retry without paying again.
 *
 * Idempotent server-side: each deduct can only be refunded once. Failures
 * here are best-effort - if the refund call itself fails we log and move
 * on, since the user already saw the AI error and surfacing a second
 * "refund failed" toast on top of that just makes the UX worse. The
 * server keeps the deduct event_log row either way for support.
 */
export async function refundDeduct(deductId: string): Promise<boolean> {
  if (!deductId) return false;
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetch(`${AUTH_URL}/auth/refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ deduct_id: deductId }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Fire-and-forget AI-action wall-time + outcome metric. Backed by Workers
// Analytics Engine on the auth worker side; queryable via the SQL/GraphQL
// API or the dashboard. Best-effort - never blocks the UI.
//
// Fires for anon users too. The auth gate that was here previously hid
// every freemium retailor from the data set. Anon writes attribute to the
// synthetic 'anon' index server-side.
//
// Modes correspond to user-triggered AI actions, not just full retailors:
//   - full           Step 4 full-resume retailor
//   - section        Step 3 section-by-section group rewrite
//   - inline         Step 3 single-bullet inline rewrite
//   - cover_letter   Cover letter generation from Step 4
//   - vibe_template  Custom-vibe HTML template generation from Step 4
export async function logRetailorMetric(
  outcome: 'success' | 'failure',
  mode: 'full' | 'section' | 'inline' | 'cover_letter' | 'vibe_template' | 'interview_prep',
  durationMs: number,
  errorReason?: string,
): Promise<void> {
  const token = getToken();
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    await fetch(`${AUTH_URL}/auth/metric/retailor`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ outcome, mode, duration_ms: durationMs, ...(errorReason ? { error_reason: errorReason } : {}) }),
    });
  } catch {
    /* observability is best-effort */
  }
}

export type FeedbackOutcome =
  | { ok: true }
  | { ok: false; reason: 'rate_limited' | 'too_long' | 'empty' | 'error'; message?: string };

/**
 * In-product feedback. Fire-and-forget from the user's perspective; we POST
 * to /auth/feedback which console.logs the payload into worker logs (no D1).
 * Attaches the session token if present so signed-in feedback carries a user
 * id server-side. Returns a typed outcome so the widget can branch on the
 * 429 / 400 cases for a useful toast message.
 */
export async function sendFeedback(message: string, pagePath?: string): Promise<FeedbackOutcome> {
  const token = getToken();
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${AUTH_URL}/auth/feedback`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message, path: pagePath }),
    });
    if (res.ok) return { ok: true };
    if (res.status === 429) return { ok: false, reason: 'rate_limited' };
    const data = await res.json().catch(() => ({})) as { error?: string };
    if (res.status === 400 && /too long/i.test(data.error || '')) return { ok: false, reason: 'too_long' };
    if (res.status === 400) return { ok: false, reason: 'empty' };
    return { ok: false, reason: 'error', message: data.error };
  } catch (e) {
    return { ok: false, reason: 'error', message: e instanceof Error ? e.message : 'Network error' };
  }
}

/**
 * Cheap point lookup to verify a ref code maps to a real user. Used by
 * SignIn to gate the "You were referred!" banner so we don't promise a
 * bonus that won't actually apply at /auth/verify-code time.
 *
 * Returns false on any error (network, 5xx, malformed response) - the
 * banner is a marketing affordance, not a hard gate, so silently failing
 * closed is the right default.
 */
export async function validatePromo(code: string): Promise<boolean> {
  if (!code) return false;
  try {
    const res = await fetch(`${AUTH_URL}/auth/validate-promo?code=${encodeURIComponent(code)}`);
    if (!res.ok) return false;
    const data = await res.json();
    return !!data.valid;
  } catch {
    return false;
  }
}

export async function sendCode(email: string): Promise<void> {
  const res = await fetch(`${AUTH_URL}/auth/send-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Failed to send code');
  }
}

export interface InviteApplied {
  days: number;
  expires_at: number;
  label: string | null;
}

export async function verifyCode(
  email: string,
  code: string,
  promo?: string,
  src?: string,
  invite?: string
): Promise<{ token: string; user: AuthUser; isNewUser?: boolean; invite_applied?: InviteApplied | null; invite_error?: string | null }> {
  const res = await fetch(`${AUTH_URL}/auth/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email,
      code,
      ...(promo && { promo }),
      ...(src && { src }),
      ...(invite && { invite }),
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Verification failed');
  setToken(data.token);
  try { localStorage.setItem(USER_KEY, JSON.stringify(data.user)); } catch { /* quota */ }
  return data;
}

export async function getSession(): Promise<AuthUser | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetch(`${AUTH_URL}/auth/session`, {
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'include',
    });
    if (!res.ok) {
      clearToken();
      return null;
    }
    const data = await res.json();
    try { localStorage.setItem(USER_KEY, JSON.stringify(data.user)); } catch { /* quota */ }
    return data.user;
  } catch {
    return null;
  }
}

// --- Saved Resumes ---

export interface SavedResume {
  id: string;
  name: string;
  parent_resume_id: string | null;
  job_title: string | null;
  job_company: string | null;
  created_at: number;
  updated_at?: number;
}

export async function listResumes(): Promise<SavedResume[]> {
  const token = getToken();
  if (!token) return [];
  const res = await fetch(`${AUTH_URL}/api/resumes`, { headers: { 'Authorization': `Bearer ${token}` } });
  if (!res.ok) return [];
  const data = await res.json();
  return data.resumes || [];
}

export async function saveResume(
  name: string,
  resumeJson: object,
  options?: {
    parentId?: string | null;
    jobTitle?: string | null;
    jobCompany?: string | null;
    jobDescription?: string | null;
  }
): Promise<{ id: string; parent_resume_id: string | null }> {
  const token = getToken();
  if (!token) throw new Error('Not signed in');
  const res = await fetch(`${AUTH_URL}/api/resumes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({
      name,
      resume_json: JSON.stringify(resumeJson),
      parent_id: options?.parentId ?? null,
      job_title: options?.jobTitle ?? null,
      job_company: options?.jobCompany ?? null,
      job_description: options?.jobDescription ?? null,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save');
  return data;
}

export async function loadResume(id: string): Promise<object | null> {
  const token = getToken();
  if (!token) return null;
  const res = await fetch(`${AUTH_URL}/api/resumes/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
  if (!res.ok) return null;
  const data = await res.json();
  try { return JSON.parse(data.resume.resume_json); } catch { return null; }
}

export async function deleteResume(id: string): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  const res = await fetch(`${AUTH_URL}/api/resumes/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return res.ok;
}

/**
 * Log a referral-link share to the backend for conversion attribution.
 * Fire-and-forget: swallows failures so a network hiccup never blocks
 * opening the share sheet. Anonymous users (no token) are silently skipped,
 * since there's no user row to attribute the share to.
 */
export function logReferralShare(platform: string): void {
  const token = getToken();
  if (!token) return;
  fetch(`${AUTH_URL}/auth/share-logged`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ platform }),
  }).catch(() => {});
}

export async function logout(): Promise<void> {
  const token = getToken();
  if (token) {
    await fetch(`${AUTH_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'include',
    }).catch(() => {});
  }
  clearToken();
  localStorage.removeItem(USER_KEY);
}
